import * as THREE from 'three';
import { World } from '../worlds/World';
import * as ObjectCreation from '../ObjectCreation';
import * as TextCreation from '../TextCreation';
import { registerScenePolicy } from '../../../core/ScenePolicies';
import { global } from '../../../core/GlobalState';
import { OrbFactory } from '../../../orbs/OrbFactory';
function spawnAbstractAlgebraNesting(scene) {
    const groupOrb = OrbFactory.createOrbByType('GROUP_ORB', 'Group');
    if (!groupOrb) {
        console.warn('[MathWorld] GROUP_ORB missing from OrbTypes');
        return;
    }
    groupOrb.setPosition(new THREE.Vector3(-1200, 1000, 3000));
    groupOrb.addToScene(scene);
    return groupOrb;
}
function spawnNumberSystemNesting(scene) {
    const realOrb = OrbFactory.createOrbByType('REAL_NUMBER_ORB', 'ℝ reals');
    if (!realOrb) {
        console.warn('[MathWorld] REAL_NUMBER_ORB missing from OrbTypes');
        return;
    }
    realOrb.setPosition(new THREE.Vector3(2200, 700, 1000));
    realOrb.addToScene(scene);
    return realOrb;
}
function spawnAnalysisNesting(scene) {
    const analysisOrb = OrbFactory.createOrbByType('ANALYSIS_REGULARITY_ORB', 'Analysis / Regularity');
    if (!analysisOrb) {
        console.warn('[MathWorld] ANALYSIS_REGULARITY_ORB missing from OrbTypes');
        return;
    }
    analysisOrb.setPosition(new THREE.Vector3(-1500, 1200, 1400));
    analysisOrb.addToScene(scene);
    return analysisOrb;
}
function createFetcherButtons(scene) {
    const origin = new THREE.Vector3(-300, 150, -300);
    const ui = {
        arcRadius: 170,
        lift: 12,
        miniScale: 0.18,
        miniRadius: 12,
    };
    const entries = [
        { fetcher: 'desmosOrbs', orbType: 'DESMOS_ORB', label: 'Desmos' },
        { fetcher: 'mathOrbs', orbType: 'MATH_ORB', label: 'Math' },
    ];
    const total = entries.length;
    const start = Math.PI * 0.55;
    const end = Math.PI * 0.2;
    entries.forEach((entry, index) => {
        var _a;
        const t = total === 1 ? 0.5 : index / (total - 1);
        const angle = start + (end - start) * t;
        const pos = new THREE.Vector3(origin.x + Math.cos(angle) * ui.arcRadius, origin.y + ui.lift, origin.z + Math.sin(angle) * ui.arcRadius);
        const button = OrbFactory.createMiniOrbByType(entry.orbType, entry.label, {}, ui.miniScale, ui.miniRadius);
        if (!button)
            return;
        button.setPosition(pos);
        button.addAction({
            trigger: 'onActivate',
            kind: 'sprayViaOrbHarnessed',
            params: {
                byType: 'BLACKHOLE',
                first: true,
                fetcher: entry.fetcher,
                args: (_a = entry.args) !== null && _a !== void 0 ? _a : {},
                baseVelocity: 5,
                sprayCoefficient: 2100,
                timeGap: global.baseTimeGap,
                useCache: true,
            },
        });
        button.addToScene(scene);
        requestAnimationFrame(() => {
            OrbFactory.normalizeOrbSize(button, ui.miniRadius, { includeSprites: false });
            requestAnimationFrame(() => {
                OrbFactory.normalizeOrbSize(button, ui.miniRadius, { includeSprites: false });
            });
        });
    });
}
function buildMathWorld(context) {
    const { scene } = context;
    scene.background = new THREE.Color(0x152c8a);
    scene.fog = new THREE.Fog(0x2340b8, 0, 32000);
    const { mirror, tintOverlay } = ObjectCreation.createMirrorFloor(1800, {
        baseColor: 0xc7d4ff,
        y: 0.04,
        dpr: 1.5,
        textureScale: 0.92,
        clipBias: 0.002,
    });
    const mathTint = tintOverlay.material;
    mathTint.opacity = 0.2;
    mathTint.color = new THREE.Color(0x5f8bff);
    scene.add(mirror);
    scene.add(tintOverlay);
    const axis = ObjectCreation.createWorldAxis(20000, 20000, 1000, {
        showGrid: true,
        gridPrimaryColor: 0xa9c3ff,
        gridSecondaryColor: 0x4f6de3,
        gridOpacity: 0.45,
        gridYOffset: 0.08,
        axisColor: 0x4f6de3,
        showSubdivisions: false,
    });
    scene.add(axis);
    const ambient = new THREE.HemisphereLight(0xc0d4ff, 0x6b8cff, 0.9);
    const key = new THREE.DirectionalLight(0xffffff, 0.65);
    key.position.set(-2400, 3600, 1800);
    const fill = new THREE.DirectionalLight(0x9fc6ff, 0.4);
    fill.position.set(2200, 2600, -1800);
    scene.add(ambient, key, fill);
    const title = TextCreation.createTextSprite('Mathematics Archive', 3.4, new THREE.Vector3(0, 560, -900), 5, '64px "Times New Roman"', '#e4f1ff', '#0a1230');
    scene.add(title);
    createFetcherButtons(scene);
    spawnAbstractAlgebraNesting(scene);
    spawnNumberSystemNesting(scene);
    spawnAnalysisNesting(scene);
    const torusKnot = new THREE.Mesh(new THREE.TorusKnotGeometry(320, 96, 220, 48), new THREE.MeshStandardMaterial({
        color: 0x5ce4ff,
        metalness: 0.55,
        roughness: 0.25,
        emissive: 0x123759,
    }));
    torusKnot.position.set(0, 480, 480);
    torusKnot.rotation.x = Math.PI / 3;
    scene.add(torusKnot);
    const polyhedron = new THREE.Mesh(new THREE.IcosahedronGeometry(280, 2), new THREE.MeshStandardMaterial({
        color: 0x2f9dff,
        emissive: 0x103e8f,
        metalness: 0.4,
        roughness: 0.2,
        wireframe: true,
    }));
    polyhedron.position.set(960, 520, 220);
    scene.add(polyhedron);
    registerScenePolicy('math', { floorY: 2 });
}
export const MathWorld = new World({
    slug: 'math',
    label: 'Mathematics',
    sceneName: 'math',
    build: buildMathWorld,
});
global.worldRegistry.registerWorld(MathWorld);
